import React, { useState } from 'react';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import dayjs from 'dayjs';
import { useAdvanceOperationalPeriod } from 'hooks/incident/useIncidentManagement';
import { useCurrentOperationalPeriod } from 'components/IAP/hooks/incidentHooks';
import {
  useAddIncidentCalendarEvent,
  eventTypes,
} from './hooks/incidentCalendarHooks';
import { v7 } from 'uuid';
import { useIncident } from './hooks/incidentHooks';
import {
  PeriodSection,
  ReadOnlyTimeTile,
} from 'components/IncidentConfig/operationalPeriodModalUi';
import { useGetDraftRosterMembersForIncident } from './hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { postExportPaginatedIap } from 'hooks/incident/useExportPaginatedIAP';
import { postPersistIcsFormExport } from 'hooks/incident/useExportForm';
import {
  selectGroupGuid,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';
import { useMemo } from 'react';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

type AdvanceOperationalPeriodModalProps = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  data?: { id: string };
};

export const AdvanceOperationalPeriodModal: React.FC<
  AdvanceOperationalPeriodModalProps
> = ({ showModal, setShowModal, data }) => {
  const [date, setDate] = useState<Date | string | null>(null);
  const { captureMapSketchFile } = useUscgMapContext();
  const advanceOperationalPeriod = useAdvanceOperationalPeriod();
  const calendarMutation = useAddIncidentCalendarEvent();
  const { data: incident } = useIncident();
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const currentPeriodNumber = currentPeriod?.period_number ?? 0;
  const groupGuid = useAppSelector(selectGroupGuid);
  const user = useAppSelector(selectUser);

  const { data: exportedForms = [] } = useExportedFormFiles(incident?.id);

  const nextPeriodNumber = currentPeriodNumber + 1;

  const hasNextPeriodIap = useMemo(() => {
    const prefix = `IAP-OP${nextPeriodNumber}-`;
    return exportedForms.some((f) => f.file_name.startsWith(prefix));
  }, [nextPeriodNumber, exportedForms]);

  const createCalendarEvents = async (d: Date) => {
    if (!incident) return;
    const events = eventTypes.filter((type) => type.title !== 'Other');
    for (let i = 0; i < events.length; i++) {
      calendarMutation.mutateAsync({
        id: v7(),
        incidentId: incident.id,
        title: events[i].title,
        purpose: events[i].agenda,
        color: events[i].code,
        start: dayjs(d).add(i, 'hour').toDate(),
        end: dayjs(d)
          .add(i + 2, 'hour')
          .toDate(),
        location: 'TBD',
        externalAttendees: '',
        eventTypeLabel: events[i].title,
      });
    }
  };

  const onAdvanceOperationalPeriod = async () => {
    if (!data?.id || date == null) return;

    const endDateTime = date instanceof Date ? date : new Date(date as string);
    await createCalendarEvents(endDateTime);

    await advanceOperationalPeriod.mutateAsync({
      incidentId: data.id,
      anticipatedEndDateTime: endDateTime.toISOString(),
      groupGuid,
    });

    // Generate IAP if it doesn't exist
    if (!hasNextPeriodIap) {
      const formnameStem = `${(incident as { name?: string })?.name ?? 'Incident'} IAP`;

      const mapSketch = await captureMapSketchFile();
      const exportResult = await postExportPaginatedIap(
        {
          incident,
          user,
          groupGuid,
          formnameStem,
          mapSketch,
        },
        nextPeriodNumber
      );

      await postPersistIcsFormExport(
        {
          incident,
          user,
          groupGuid,
        },
        {
          pdfBase64: exportResult.data,
          workspaceFormname: 'IAP',
          periodNumber: nextPeriodNumber,
        }
      );
    }

    setShowModal(false);
  };

  const { data: draftRoster = [] } = useGetDraftRosterMembersForIncident();
  const hasDraftRoster = draftRoster.length > 0;

  const startedCurrent = currentPeriod?.start_date_time
    ? dayjs(currentPeriod.start_date_time).format('MM/DD/YYYY [at] h:mm A')
    : '—';

  const anticipatedCurrent = currentPeriod?.anticipated_end_date_time
    ? dayjs(currentPeriod.anticipated_end_date_time).format(
        'MM/DD/YYYY [at] h:mm A'
      )
    : '—';

  return (
    <CustomModal
      show={showModal}
      heading="Advance operational period"
      onClose={() => setShowModal(false)}
      onConfirm={onAdvanceOperationalPeriod}
      saveButton="Advance"
      buttonDisable={!date || advanceOperationalPeriod.isPending}
    >
      <div>
        <PeriodSection
          title={`Period ${currentPeriodNumber}${
            currentPeriodNumber === 0
              ? ' (ICS-201 development and briefing)'
              : ''
          }`}
          badge={<span className="badge bg-primary">Current</span>}
        >
          <div className="row g-2">
            <div className="col-md-6">
              <ReadOnlyTimeTile
                label="Started"
                valueFormatted={startedCurrent}
              />
            </div>
            <div className="col-md-6">
              <ReadOnlyTimeTile
                label="Anticipated end"
                valueFormatted={anticipatedCurrent}
              />
            </div>
          </div>
        </PeriodSection>

        <PeriodSection
          title={`Period ${nextPeriodNumber}`}
          badge={<span className="badge bg-info text-dark">Next</span>}
          highlight
        >
          <div className="bg-dark bg-opacity-25 rounded p-3">
            <div className="text-uppercase txt--sml text-weak mb-3">
              Anticipated end
            </div>
            <div className="form-label">Date &amp; time</div>
            <StylishDateTimePicker
              value={
                date == null
                  ? null
                  : date instanceof Date
                    ? date
                    : new Date(date as string)
              }
              onChange={(newDate) => {
                setDate(newDate ? new Date(newDate) : null);
              }}
              type="datetime-local"
              required={true}
              disabled={advanceOperationalPeriod.isPending}
            />
          </div>
        </PeriodSection>

        {hasDraftRoster && (
          <div className="alert alert-warning mt-3 mb-0">
            <strong>
              A draft roster exists for this incident. Advancing the operational
              period will make the draft roster active.
            </strong>
          </div>
        )}

        <div className="text-weak txt--sml mt-2 mb-0">
          Advancing to period {nextPeriodNumber}
        </div>
      </div>
    </CustomModal>
  );
};
